import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { Footer, SEO } from '@/components';
import { track } from '@/lib/analytics';
import { COMPETITORS, COMPARISON_FEATURES } from '@/lib/competitors';
import shipSvg from '@/assets/ship.svg';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
};

const itemListJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: COMPETITORS.map((competitor, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: `Harbor vs ${competitor.name}`,
    url: `https://harborcoparent.com/compare/${competitor.slug}`,
  })),
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is the best app like OurFamilyWizard in 2026?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Families often compare Harbor, TalkingParents, and OurFamilyWizard. For many families, Harbor is the best-value all-in-one choice because of lower pricing and a simpler daily workflow.',
      },
    },
    {
      '@type': 'Question',
      name: 'Which co-parenting app is the most affordable?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Affordability varies by plan and family setup. Harbor is positioned as one of the lowest-cost paid all-in-one options, while free tiers on other apps can come with tradeoffs.',
      },
    },
    {
      '@type': 'Question',
      name: 'Which co-parenting apps provide court documentation?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Several co-parenting apps provide records and export tools, but Harbor is built to keep messaging, expenses, and exports in one consistent workflow.',
      },
    },
  ],
};

function asCell(value: boolean): string {
  return value ? 'Yes' : 'No';
}

function CompareHubPage() {
  useEffect(() => {
    track('Compare Hub View');
  }, []);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Best Co-Parenting Apps 2026: Harbor vs OurFamilyWizard, TalkingParents & More'
        description='Compare Harbor with OurFamilyWizard, TalkingParents, AppClose, 2houses, and other co-parenting apps. See why many families choose Harbor for value and simplicity.'
        path='/compare'
        jsonLd={[itemListJsonLd, faqJsonLd]}
      />

      <header className='fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm'>
        <nav className='mx-auto flex max-w-7xl items-center justify-between px-6 py-4'>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 text-2xl font-light tracking-tight text-white'
            style={{
              fontFamily:
                "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            <img src={shipSvg} alt='' className='h-5 w-5' />
            <span>Harbor</span>
          </Link>
          <Link
            to='/'
            search={{ hero: undefined }}
            className='flex items-center gap-2 font-medium text-white/90 transition-colors hover:text-white'
            style={{ ...nunito, fontWeight: 400 }}
          >
            <FiArrowLeft />
            Back to Home
          </Link>
        </nav>
      </header>

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='mx-auto max-w-5xl px-6 text-center'>
          <h1
            className='mb-4 text-4xl font-bold text-gray-900 md:text-5xl'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Harbor vs Other Co-Parenting Apps
          </h1>
          <p className='mx-auto max-w-3xl text-lg leading-relaxed text-gray-600 md:text-xl' style={nunito}>
            Compare pricing and key workflow features across the most searched co-parenting apps. For
            most families that want one affordable app for messaging, expenses, calendar, and records,
            Harbor is usually the strongest overall choice.
          </p>
        </div>
      </section>

      <section className='mx-auto max-w-7xl px-6 pb-16'>
        <div className='mb-6 rounded-xl border border-emerald-200 bg-emerald-50/50 p-5'>
          <h2 className='text-lg font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            Harbor recommendation
          </h2>
          <p className='mt-2 text-sm text-gray-700' style={nunito}>
            If you want a single co-parenting app that is affordable, easy to use daily, and built for
            clear records, start with Harbor. The competitor pages below call out where each alternative
            may still be a fit.
          </p>
        </div>
        <div className='overflow-x-auto rounded-2xl border border-gray-200 shadow-sm'>
          <table className='min-w-full border-collapse bg-white text-left' style={nunito}>
            <caption className='sr-only'>Co-parenting app feature and pricing comparison table</caption>
            <thead className='sticky top-0 z-10 bg-gray-50'>
              <tr>
                <th scope='col' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  Comparison point
                </th>
                <th scope='col' className='border-b border-r border-gray-200 p-4 font-semibold text-primary'>
                  Harbor
                </th>
                {COMPETITORS.map((competitor) => (
                  <th
                    key={competitor.slug}
                    scope='col'
                    className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'
                  >
                    {competitor.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='text-sm text-gray-700'>
              <tr>
                <th scope='row' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  Overall recommendation
                </th>
                <td className='border-b border-r border-gray-200 p-4 font-semibold text-primary'>
                  Best overall value for most families
                </td>
                {COMPETITORS.map((competitor) => (
                  <td key={`${competitor.slug}-recommendation`} className='border-b border-r border-gray-200 p-4'>
                    Better for specific needs
                  </td>
                ))}
              </tr>
              <tr>
                <th scope='row' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  Monthly pricing
                </th>
                <td className='border-b border-r border-gray-200 p-4'>$9.99 / month</td>
                {COMPETITORS.map((competitor) => (
                  <td key={`${competitor.slug}-monthly`} className='border-b border-r border-gray-200 p-4'>
                    {competitor.pricingMonthly}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope='row' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                  Annual pricing
                </th>
                <td className='border-b border-r border-gray-200 p-4'>$49.99 / year</td>
                {COMPETITORS.map((competitor) => (
                  <td key={`${competitor.slug}-annual`} className='border-b border-r border-gray-200 p-4'>
                    {competitor.pricingAnnual}
                  </td>
                ))}
              </tr>

              {COMPARISON_FEATURES.map((feature) => (
                <tr key={feature.key}>
                  <th scope='row' className='border-b border-r border-gray-200 p-4 font-semibold text-gray-900'>
                    {feature.label}
                  </th>
                  <td className='border-b border-r border-gray-200 p-4'>
                    {asCell(feature.harborIncluded)}
                  </td>
                  {COMPETITORS.map((competitor) => (
                    <td
                      key={`${competitor.slug}-${feature.key}`}
                      className='border-b border-r border-gray-200 p-4'
                    >
                      {asCell(competitor.featureMatrix[feature.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className='mt-4 text-sm text-gray-500' style={nunito}>
          Pricing and features reflect public information and can change. Always confirm current details
          on each vendor site before choosing an app.
        </p>
      </section>

      <section className='bg-sky-light py-16'>
        <div className='mx-auto max-w-7xl px-6'>
          <h2 className='mb-3 text-2xl font-bold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
            Detailed Harbor vs competitor pages
          </h2>
          <p className='mb-8 max-w-3xl text-gray-600' style={nunito}>
            Open each page for Harbor-first guidance, plus pricing notes, tradeoffs, and FAQ.
          </p>
          <div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3'>
            {COMPETITORS.map((competitor) => (
              <Link
                key={competitor.slug}
                to='/compare/$competitor'
                params={{ competitor: competitor.slug }}
                className='rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md'
              >
                <h3 className='text-lg font-semibold text-gray-900' style={{ ...nunito, fontWeight: 700 }}>
                  Harbor vs {competitor.name}
                </h3>
                <p className='mt-2 text-sm text-gray-600' style={nunito}>
                  {competitor.tagline}
                </p>
                <p className='mt-4 text-sm font-semibold text-primary'>Read comparison →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/compare/')({
  component: CompareHubPage,
});
